import {
  Directive,
  ElementRef,
  HostListener,
  Renderer2,
  inject,
  input,
  model,
} from '@angular/core';

export interface ResizeComputeInput {
  edge: 'left' | 'right';
  startWidth: number;
  startX: number;
  currentX: number;
  min: number;
  max: number;
  collapseThreshold?: number;
}

export interface ResizeComputeResult {
  width: number;
  collapsed: boolean;
}

/**
 * Pure width/collapse math for a drag-resize handle, factored out of the
 * directive so it is testable without simulating real PointerEvents (jsdom
 * does not implement setPointerCapture).
 *
 * 'right' edge: dragging right (currentX > startX) widens the panel.
 * 'left' edge: dragging left (currentX < startX) widens the panel (mirrored —
 * used when the panel is to the right of its own handle, e.g. a rail whose
 * grab strip sits on its left edge).
 */
export function computeResizeState(input: ResizeComputeInput): ResizeComputeResult {
  const delta = input.currentX - input.startX;
  const signedDelta = input.edge === 'right' ? delta : -delta;
  const rawWidth = input.startWidth + signedDelta;
  const width = Math.min(input.max, Math.max(input.min, rawWidth));
  const collapsed = input.collapseThreshold !== undefined && rawWidth < input.collapseThreshold;
  return { width: collapsed ? input.min : width, collapsed };
}

/**
 * Drag-to-resize handle. Place as a thin grab-strip element on the resized
 * panel's edge:
 *
 *   <div cosmosResizeHandle edge="right" [(width)]="panelWidth"
 *        [(collapsed)]="panelCollapsed" [min]="180" [max]="560"
 *        [default]="320" [collapseThreshold]="180"></div>
 *
 * Ported from feat/ai-chat's help-rail-resize.directive.ts (pointer-capture
 * drag + a full-viewport transparent overlay so pointermove keeps firing over
 * iframes/maps/other pointer-capturing content), generalised to either edge
 * and to an optional collapse-on-drag threshold. Does NOT persist width to
 * storage — the consumer's model binding owns that (see UiPrefsService and
 * its consumers in Phase 3/4).
 */
@Directive({ selector: '[cosmosResizeHandle]' })
export class ResizeHandleDirective {
  private readonly host = inject(ElementRef<HTMLElement>);
  private readonly renderer = inject(Renderer2);

  readonly edge = input<'left' | 'right'>('right');
  readonly min = input<number>(180);
  readonly max = input<number>(560);
  readonly default = input<number>(320);
  readonly collapseThreshold = input<number | undefined>(undefined);

  readonly width = model<number>(320);
  readonly collapsed = model<boolean>(false);

  private dragging = false;
  private startX = 0;
  private startWidth = 0;
  private overlay: HTMLElement | null = null;

  @HostListener('pointerdown', ['$event'])
  onPointerDown(e: PointerEvent): void {
    if (e.button !== 0) return;
    e.preventDefault();
    this.dragging = true;
    this.startX = e.clientX;
    this.startWidth = this.width();
    this.host.nativeElement.setPointerCapture(e.pointerId);
    this.addOverlay();
  }

  @HostListener('pointermove', ['$event'])
  onPointerMove(e: PointerEvent): void {
    if (!this.dragging) return;
    const result = computeResizeState({
      edge: this.edge(),
      startWidth: this.startWidth,
      startX: this.startX,
      currentX: e.clientX,
      min: this.min(),
      max: this.max(),
      collapseThreshold: this.collapseThreshold(),
    });
    this.width.set(result.width);
    this.collapsed.set(result.collapsed);
  }

  @HostListener('pointerup', ['$event'])
  @HostListener('pointercancel', ['$event'])
  onPointerUp(e: PointerEvent): void {
    if (!this.dragging) return;
    this.dragging = false;
    this.host.nativeElement.releasePointerCapture?.(e.pointerId);
    this.removeOverlay();
  }

  @HostListener('dblclick')
  onDoubleClick(): void {
    this.width.set(this.default());
    this.collapsed.set(false);
  }

  private addOverlay(): void {
    const el = this.renderer.createElement('div') as HTMLElement;
    this.renderer.setStyle(el, 'position', 'fixed');
    this.renderer.setStyle(el, 'inset', '0');
    this.renderer.setStyle(el, 'z-index', '2000');
    this.renderer.setStyle(el, 'cursor', 'col-resize');
    this.renderer.appendChild(document.body, el);
    this.overlay = el;
  }

  private removeOverlay(): void {
    if (this.overlay) {
      this.renderer.removeChild(document.body, this.overlay);
      this.overlay = null;
    }
  }
}
